"use client"

import React, { useRef, useState } from 'react';

import __ from '../utils/translation';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Upload, ImageIcon, X, AlertCircle } from 'lucide-react';

interface UploadedImage {
  id: string;
  type: string;
  dataUrl: string;
}

const UploadSettings = ({ canvas }: { canvas: any }) => {

  const inputFile = useRef<HTMLInputElement>(null)
  const dropArea = useRef<HTMLDivElement>(null)
  const [images, setImages] = useState<UploadedImage[]>([])
  const [isDragging, setIsDragging] = useState(false)


  // check if HTML5 File API is supported by the browser
  if (typeof window !== 'undefined' && !window.File && !window.FileReader && !window.FileList && !window.Blob) {
    return (
      <Card className="border-destructive/50 bg-destructive/5">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center gap-4 text-center">
            <AlertCircle className="h-12 w-12 text-destructive" />
            <p className="text-sm text-muted-foreground">
              {__(`Your browser doesn't support file upload. Please upgrade to a modern browser, for example:`)}{' '}
              <a 
                href="https://www.google.com/chrome/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-medium text-primary underline underline-offset-4 hover:text-primary/80"
              >
                Chrome!
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  const removeImage = (id: string) => {
    setImages(prev => prev.filter(img => img.id !== id))
  }

  const processFiles = (e: React.DragEvent | React.ChangeEvent, files: FileList | null) => {
    e.preventDefault()
    setIsDragging(false)

    if (!files) return

    const allowedTypes = ['image/jpeg', 'image/png', 'image/svg+xml', 'application/pdf']

    for (let file of Array.from(files)) {
      // check type
      if (!allowedTypes.includes(file.type)) continue

      let reader = new FileReader()

      // handle svg
      /*if (file.type === 'image/svg+xml'  || file.type === 'application/pdf') {
        //var svg;
        reader.onload = (f) => {
          fabric.loadSVGFromString(f.target.result, function(objects, options) {
            const svg = fabric.util.groupSVGElements(objects, options);
            // canvas scale to svg
            //canvas.setWidth(svg.width);
            //canvas.setHeight(svg.height);
            //or svg scale to canvas
            svg.scaleToHeight(canvas.height);

            //Glitch, need to do this twice to make things right
            fabric.loadSVGFromString(f.target.result,
                (objects, options) => {
                    var group = new fabric.Group(objects, options);
                    group.scaleToHeight(canvas.getHeight());
                    var items = group._objects;
                    group._restoreObjectsState();
            
                    for (let i = 1; i < items.length; i++) {
                      items[i].lockMovementX = true;
                      items[i].lockMovementY = true;
                      items[i].lockScalingX = true;
                      items[i].lockScalingY = true;
                      items[i].lockRotation = true;
                      items[i].hasControls = false;
                      items[i].default = true;

                      canvas.add(items[i]);
                    }
                    
                    canvas.renderAll();
                });
            
          });
        }
        reader.readAsText(file)
        continue
      }*/

      // handle image, read file, add to canvas
      reader.onload = (f) => {

        //var files = f.target.file
        //var filename = files[0].name
        //var extension = files.type


        if (file.type == "image/svg+xml")
          (window as any).fileSVG = file;

        const dataUrl = f.target?.result as string
        setImages(prev => [...prev, {
          id: crypto.randomUUID(),
          type: file.type,
          dataUrl: dataUrl
        }])

        /*fabric.Image.fromURL(f.target.result, (img) => {
          img.set({left: 0, top: 0})
          img.scaleToHeight(300)
          img.scaleToWidth(300)
          canvas.add(img)

          canvas.renderAll()
          canvas.trigger('object:modified')
        })*/
      }



      reader.readAsDataURL(file)
    }
  }


  return (
    <Card className="border-none bg-card/85 shadow-none p-4 h-full rounded-none">
      <CardHeader className="px-0 pt-0">
        <CardTitle className="text-lg font-semibold tracking-tight lang" data-en="UPLOAD IMAGE" data-pt="ENVIAR IMAGEM" data-es="CARGAR IMAGEN">
          {__('UPLOAD IMAGE')}
        </CardTitle>
        {/**<CardDescription className="text-sm text-muted-foreground lang" data-en="File upload occurs via the HTML5 File API, so we do not use the server to store user images. You can also copy and paste an image from the clipboard!" data-pt="O upload dos arquivos ocorre por meio da API de arquivos HTML5, portanto, não utilizamos o servidor para armazenar imagens do usuário. Você também pode copiar e colar uma imagem da área de transferência!" data-es="La carga de archivos se realiza a través de la API de archivos HTML5, por lo que no utilizamos el servidor para almacenar imágenes de los usuarios. ¡También puedes copiar y pegar una imagen desde el portapapeles!">
          {__(`File upload occurs via the HTML5 File API, so we do not use the server to store user images. You can also copy and paste an image from the clipboard!`)}
        </CardDescription> */}
      </CardHeader>

      <CardContent className="space-y-6 px-0 pb-0">
        <div 
          ref={dropArea} 
          onClick={() => inputFile.current?.click()}
          onDrop={(e) => processFiles(e, e.dataTransfer.files)}
          onDragOver={(e) => {
            e.preventDefault()
            setIsDragging(true)
          }}
          onDragLeave={(e) => {
            e.preventDefault()
            setIsDragging(false)
          }}
          className={`group relative flex min-h-[180px] cursor-pointer flex-col items-center justify-center gap-4 rounded-xl border-2 border-dashed px-6 py-10 text-center transition-all duration-200 ${
            isDragging 
              ? 'border-primary bg-primary/5' 
              : 'border-muted-foreground/25 bg-muted/30 hover:border-primary/50 hover:bg-muted/50'
          }`}
        >
          <div className={`flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary transition-transform duration-200 ${isDragging ? 'scale-110' : 'group-hover:scale-110'}`}>
            <Upload className="h-7 w-7" />
          </div>
          <div className="space-y-1.5">
            <p className="text-sm font-medium text-foreground lang" data-en="Drag & drop files or click to browse" data-pt="Arraste e solte arquivos ou clique para navegar" data-es="Arrastre y suelte archivos o haga clic para explorar">
              {__('Drag & drop files or click to browse. JPG, PNG or SVG only!')}
            </p>
            <p className="text-xs text-muted-foreground">
              JPG, PNG, SVG
            </p>
          </div>
        </div>

        <input 
          type="file" 
          onChange={(e) => processFiles(e, e.target.files)} 
          multiple
          accept="image/jpeg,image/png,image/svg+xml"
          ref={inputFile} 
          className="hidden" 
        />

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <ImageIcon className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm font-medium text-foreground lang img-list" data-en="Image List:" data-pt="Lista de imagens:" data-es="Lista de imágenes:">
              Image List:
            </p>
          </div>

          <div id="image-holder" className="flex flex-wrap gap-3">
            {images.map((img) => (
              <div 
                key={img.id}
                data-type={img.type}
                data-value={img.dataUrl}
                className="img-holder group relative aspect-square w-20 overflow-hidden rounded-lg border border-border bg-muted/50 transition-all hover:border-primary/50 hover:shadow-md"
                style={{ 
                  backgroundImage: `url(${img.dataUrl})`, 
                  backgroundSize: 'cover', 
                  backgroundPosition: 'center' 
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    removeImage(img.id)
                  }}
                  className="remove_img_holder absolute -right-1 -top-1 flex h-5 w-5 cursor-pointer items-center justify-center rounded-full bg-destructive text-destructive-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:bg-destructive/90"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  )

}


export default UploadSettings
